import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  TextField,
  Tag,
  Divider,
  Banner,
  SkeletonBodyText,
  Spinner,
  Autocomplete,
  Icon,
  Thumbnail,
  DropZone,
  Modal,
  Select,
} from '@shopify/polaris';
import { SearchIcon, EditIcon, DeleteIcon } from '@shopify/polaris-icons';
import RichTextEditor from '../components/RichTextEditor';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { StoreBadge } from '../lib/storeUtils';
import { useNavigate } from 'react-router-dom';
import {
  useTrackedProducts,
  useProductSearch,
  useGmailConnection,
  useEmailSenderPatterns,
  useGmailAccounts,
  useProductionFloorConfig,
  useThreadColors,
  useQAChecklistConfig,
  useSizeSelectionCollections,
  usePatchCollection,
  useFollowUpConfig,
  useFollowUpActivity,
} from '../hooks';
import { useAIMockupSettings, usePatchTemplates } from '../hooks/useMockups';
import type { ShopifyProduct, ProductType, FollowUpRule } from '../types';
import { STAGE_LABELS } from '../types';
import type { PatchTemplate } from '../api/mockups';
import { getWebhookStatus, registerWebhooks, searchCollections as searchCollectionsApi } from '../api/settings';
import type { WorkflowStage } from '../types';

// Badge tones for stage labels in follow-up rules
const STAGE_TONES_MAP: Record<WorkflowStage, 'info' | 'success' | 'warning' | 'attention' | 'critical' | undefined> = {
  ORDER_RECEIVED: 'info',
  DIGITAL_MOCKUP: 'info',
  AWAITING_APPROVAL: 'warning',
  APPROVED: 'success',
  DIGITIZED_MOLD_SENT: 'attention',
  DIGITIZED_MOLD_RECEIVED: 'success',
  IN_PRODUCTION: 'info',
  IN_QA: 'attention',
  READY_TO_SHIP: 'success',
  SHIPPED: 'success',
  COMPLETE: 'success',
  ARCHIVED: undefined,
};

export default function Settings() {
  const navigate = useNavigate();

  // Follow-up notifications
  const {
    config: followUpConfig,
    loading: followUpLoading,
    error: followUpError,
    saveConfig: saveFollowUpConfig,
    toggleRule: toggleFollowUpRule,
    saveRule: saveFollowUpRule,
    sendTest: sendFollowUpTest,
    actionLoading: followUpActionLoading,
  } = useFollowUpConfig();

  const {
    activity: followUpActivity,
    loading: activityLoading,
    refetch: refetchActivity,
    hasMore: activityHasMore,
    loadMore: loadMoreActivity,
  } = useFollowUpActivity();

  const [editingFollowUpRule, setEditingFollowUpRule] = useState<FollowUpRule | null>(null);
  const [editRuleSubject, setEditRuleSubject] = useState('');
  const [editRuleBody, setEditRuleBody] = useState('');
  const [editRuleDelayHours, setEditRuleDelayHours] = useState('');
  const [editRuleRepeatHours, setEditRuleRepeatHours] = useState('');
  const [editRuleMaxReminders, setEditRuleMaxReminders] = useState('');
  const [editRuleChannel, setEditRuleChannel] = useState('email');
  const [editRuleEnabled, setEditRuleEnabled] = useState(false);
  const [followUpStaffEmail, setFollowUpStaffEmail] = useState('');
  const [followUpStaffPhone, setFollowUpStaffPhone] = useState('');
  const [showFollowUpActivity, setShowFollowUpActivity] = useState(false);
  const [followUpTestSuccess, setFollowUpTestSuccess] = useState<string | null>(null);
  const [testOrderId, setTestOrderId] = useState('');

  // Sync staff fields when config loads
  useEffect(() => {
    if (followUpConfig) {
      setFollowUpStaffEmail(followUpConfig.staffEmail || '');
      setFollowUpStaffPhone(followUpConfig.staffPhone || '');
    }
  }, [followUpConfig]);

  const handleOpenEditRule = useCallback((rule: FollowUpRule) => {
    setEditingFollowUpRule(rule);
    setEditRuleSubject(rule.subjectTemplate);
    setEditRuleBody(rule.bodyTemplate);
    setEditRuleDelayHours(String(rule.delayHours || ''));
    setEditRuleRepeatHours(String(rule.repeatIntervalHours || ''));
    setEditRuleMaxReminders(String(rule.maxReminders || ''));
    setEditRuleChannel(rule.channel);
    setEditRuleEnabled(rule.enabled);
    setFollowUpTestSuccess(null);
  }, []);

  const handleSaveEditRule = useCallback(async () => {
    if (!editingFollowUpRule) return;
    const updated: FollowUpRule = {
      ...editingFollowUpRule,
      subjectTemplate: editRuleSubject,
      bodyTemplate: editRuleBody,
      channel: editRuleChannel as 'email' | 'sms',
      enabled: editRuleEnabled,
      delayHours: editRuleDelayHours ? parseInt(editRuleDelayHours, 10) : undefined,
      repeatIntervalHours: editRuleRepeatHours ? parseInt(editRuleRepeatHours, 10) : undefined,
      maxReminders: editRuleMaxReminders ? parseInt(editRuleMaxReminders, 10) : undefined,
    };
    const success = await saveFollowUpRule(updated);
    if (success) {
      setEditingFollowUpRule(null);
    }
  }, [editingFollowUpRule, editRuleSubject, editRuleBody, editRuleChannel, editRuleEnabled, editRuleDelayHours, editRuleRepeatHours, editRuleMaxReminders, saveFollowUpRule]);

  const handleSaveStaffContacts = useCallback(async () => {
    await saveFollowUpConfig({
      staffEmail: followUpStaffEmail,
      staffPhone: followUpStaffPhone,
    });
  }, [followUpStaffEmail, followUpStaffPhone, saveFollowUpConfig]);

  // Product tracking
  const {
    data: trackedProducts,
    loading: productsLoading,
    error: productsError,
    addProduct,
    updateProductType,
    removeProduct,
    actionLoading: productActionLoading,
  } = useTrackedProducts();

  // Product search
  const {
    results: searchResults,
    loading: searchLoading,
    search: searchProducts,
    clear: clearSearch,
  } = useProductSearch();

  const [productSearchValue, setProductSearchValue] = useState('');
  const [selectedProductType, setSelectedProductType] = useState<ProductType>('embroidery');

  // Handle inline product type change
  const handleProductTypeChange = useCallback(
    async (productId: string, newType: ProductType) => {
      await updateProductType(productId, newType);
    },
    [updateProductType]
  );

  // Handle product search input
  const handleProductSearchChange = useCallback(
    (value: string) => {
      setProductSearchValue(value);
      if (value.length >= 2) {
        searchProducts(value);
      } else {
        clearSearch();
      }
    },
    [searchProducts, clearSearch]
  );

  // Handle adding a product
  const handleAddProduct = useCallback(
    async (product: ShopifyProduct) => {
      const priceRange = product.priceRangeV2
        ? {
            min: product.priceRangeV2.minVariantPrice.amount,
            max: product.priceRangeV2.maxVariantPrice.amount,
            currencyCode: product.priceRangeV2.minVariantPrice.currencyCode,
          }
        : undefined;
      const success = await addProduct({
        shopifyProductId: product.id,
        shopifyProductTitle: product.title,
        shopifyProductHandle: product.handle,
        productType: selectedProductType,
        priceRange,
      });
      if (success) {
        setProductSearchValue('');
        clearSearch();
      }
    },
    [addProduct, clearSearch, selectedProductType]
  );

  // Ensure trackedProducts is always an array
  const productsArray = Array.isArray(trackedProducts) ? trackedProducts : [];

  // Filter out already tracked products from search results
  const filteredSearchResults = useMemo(() => {
    const trackedIds = new Set(productsArray.map((p) => p.shopifyProductId));
    return searchResults.filter((p) => !trackedIds.has(p.id));
  }, [searchResults, productsArray]);

  // Gmail connection
  const {
    status: gmailStatus,
    loading: gmailLoading,
    error: gmailError,
    connect: connectGmail,
    disconnect: disconnectGmail,
    actionLoading: gmailActionLoading,
  } = useGmailConnection();

  // Email sender patterns
  const {
    data: senderPatterns,
    loading: patternsLoading,
    error: patternsError,
    addPattern,
    removePattern,
    actionLoading: patternActionLoading,
  } = useEmailSenderPatterns();

  const [newPatternEmail, setNewPatternEmail] = useState('');
  const [newPatternName, setNewPatternName] = useState('');

  // Gmail Accounts (multi-account communication)
  const {
    accounts: gmailAccounts,
    loading: gmailAccountsLoading,
    error: gmailAccountsError,
    connectAccount,
    disconnectAccount,
    actionLoading: gmailAccountsActionLoading,
  } = useGmailAccounts();

  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [newAccountLabel, setNewAccountLabel] = useState('');

const handleConnectAccount = useCallback(async () => {
  const trimmedLabel = newAccountLabel.trim();
  if (!trimmedLabel) return;
  setConnectModalOpen(false);
  await connectAccount(trimmedLabel);
  setNewAccountLabel('');
}, [newAccountLabel, connectAccount]);

  // AI Mockup Settings
  const aiMockupSettings = useAIMockupSettings();
  const patchTemplates = usePatchTemplates();

  // Production Floor Config
  const productionFloorConfig = useProductionFloorConfig();
  const [newPin, setNewPin] = useState('');
  const [pinSaveSuccess, setPinSaveSuccess] = useState(false);

  // Thread Colors Config
  const threadColorsConfig = useThreadColors();
  const [newColorName, setNewColorName] = useState('');
  const [newColorHex, setNewColorHex] = useState('#000000');
  const [colorSaveSuccess, setColorSaveSuccess] = useState(false);

  // QA Checklist Config
  const qaChecklistConfig = useQAChecklistConfig();
  const [newChecklistLabel, setNewChecklistLabel] = useState('');
  const [newChecklistRequired, setNewChecklistRequired] = useState(true);
  const [qaChecklistSaveSuccess, setQaChecklistSaveSuccess] = useState(false);
  const [editedRefinementPrompt, setEditedRefinementPrompt] = useState('');
  const [refinementPromptChanged, setRefinementPromptChanged] = useState(false);
  const [refinementPromptSaveSuccess, setRefinementPromptSaveSuccess] = useState(false);
  const [templateUploadSuccess, setTemplateUploadSuccess] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplatePrompt, setNewTemplatePrompt] = useState('');

  // Template editing state
  const [editingTemplate, setEditingTemplate] = useState<PatchTemplate | null>(null);
  const [editTemplateName, setEditTemplateName] = useState('');
  const [editTemplatePrompt, setEditTemplatePrompt] = useState('');
  const [templateEditSuccess, setTemplateEditSuccess] = useState(false);

  // Size Selection Collections Config
  const sizeSelectionCollections = useSizeSelectionCollections();
  const [collectionSearchValue, setCollectionSearchValue] = useState('');
  const [sizeSelectionSaveSuccess, setSizeSelectionSaveSuccess] = useState(false);

  const handleCollectionSearchChange = useCallback(
    (value: string) => {
      setCollectionSearchValue(value);
      if (value.length >= 2) {
        sizeSelectionCollections.search(value);
      } else {
        sizeSelectionCollections.clearSearch();
      }
    },
    [sizeSelectionCollections]
  );

  const handleAddSizeSelectionCollection = useCallback(
    async (selected: string[]) => {
      const collectionId = selected[0];
      const collection = sizeSelectionCollections.searchResults.find((c) => c.id === collectionId);
      if (collection) {
        const success = await sizeSelectionCollections.addCollection(collection);
        if (success) {
          setCollectionSearchValue('');
          sizeSelectionCollections.clearSearch();
          setSizeSelectionSaveSuccess(true);
          setTimeout(() => setSizeSelectionSaveSuccess(false), 2000);
        }
      }
    },
    [sizeSelectionCollections]
  );

  const handleRemoveSizeSelectionCollection = useCallback(
    async (collectionId: string) => {
      const success = await sizeSelectionCollections.removeCollection(collectionId);
      if (success) {
        setSizeSelectionSaveSuccess(true);
        setTimeout(() => setSizeSelectionSaveSuccess(false), 2000);
      }
    },
    [sizeSelectionCollections]
  );

  // Patch Collection Config
  const patchCollectionConfig = usePatchCollection();
  const [patchCollectionSearchValue, setPatchCollectionSearchValue] = useState('');
  const [patchCollectionSaveSuccess, setPatchCollectionSaveSuccess] = useState(false);
  const [patchCollectionSearchResults, setPatchCollectionSearchResults] = useState<{ id: string; title: string; handle: string }[]>([]);
  const [patchCollectionSearchLoading, setPatchCollectionSearchLoading] = useState(false);

  const handlePatchCollectionSearchChange = useCallback(
    async (value: string) => {
      setPatchCollectionSearchValue(value);
      if (value.length >= 2) {
        setPatchCollectionSearchLoading(true);
        try {
          const results = await searchCollectionsApi(value);
          setPatchCollectionSearchResults(results);
        } catch {
          // ignore search errors
        } finally {
          setPatchCollectionSearchLoading(false);
        }
      } else {
        setPatchCollectionSearchResults([]);
      }
    },
    []
  );

  const handleSelectPatchCollection = useCallback(
    async (selected: string[]) => {
      const collectionId = selected[0];
      const collection = patchCollectionSearchResults.find((c) => c.id === collectionId);
      if (!collection) return;

      const success = await patchCollectionConfig.update(collection);
      if (!success) return;

      setPatchCollectionSearchValue('');
      setPatchCollectionSearchResults([]);
      setPatchCollectionSaveSuccess(true);
      setTimeout(() => setPatchCollectionSaveSuccess(false), 2000);
    },
    [patchCollectionSearchResults, patchCollectionConfig]
  );

  const handleRemovePatchCollection = useCallback(
    async () => {
      const success = await patchCollectionConfig.update(null);
      if (success) {
        setPatchCollectionSaveSuccess(true);
        setTimeout(() => setPatchCollectionSaveSuccess(false), 2000);
      }
    },
    [patchCollectionConfig]
  );

  // Webhook management state
  const [webhookStatus, setWebhookStatus] = useState<{
    loading: boolean;
    error: string | null;
    allRequired: boolean;
    requiredWebhooks: Array<{
      topic: string;
      address: string;
      isRegistered: boolean;
      webhookId: string | null;
    }>;
  }>({ loading: true, error: null, allRequired: false, requiredWebhooks: [] });
  const [webhookRegistering, setWebhookRegistering] = useState(false);
  const [webhookSuccess, setWebhookSuccess] = useState(false);

  // Initialize refinement prompt when settings load
  useEffect(() => {
    if (aiMockupSettings.settings?.refinementPromptTemplate) {
      setEditedRefinementPrompt(aiMockupSettings.settings.refinementPromptTemplate);
    }
  }, [aiMockupSettings.settings?.refinementPromptTemplate]);

  // Fetch webhook status on mount
  useEffect(() => {
    async function fetchWebhookStatus() {
      try {
        const status = await getWebhookStatus();
        setWebhookStatus({
          loading: false,
          error: null,
          allRequired: status.allRequired,
          requiredWebhooks: status.requiredWebhooks,
        });
      } catch (err) {
        setWebhookStatus({
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to fetch webhook status',
          allRequired: false,
          requiredWebhooks: [],
        });
      }
    }
    fetchWebhookStatus();
  }, []);

  // Handle webhook registration
  const handleRegisterWebhooks = useCallback(async () => {
    setWebhookRegistering(true);
    setWebhookSuccess(false);
    try {
      const result = await registerWebhooks();
      if (result.success) {
        setWebhookSuccess(true);
        // Refresh webhook status
        const status = await getWebhookStatus();
        setWebhookStatus({
          loading: false,
          error: null,
          allRequired: status.allRequired,
          requiredWebhooks: status.requiredWebhooks,
        });
        setTimeout(() => setWebhookSuccess(false), 3000);
      } else {
        setWebhookStatus(prev => ({
          ...prev,
          error: 'Some webhooks failed to register. Check console for details.',
        }));
        console.error('Webhook registration results:', result.results);
      }
    } catch (err) {
      setWebhookStatus(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to register webhooks',
      }));
    } finally {
      setWebhookRegistering(false);
    }
  }, []);

  // Track refinement prompt changes
  const handleRefinementPromptChange = useCallback(
    (value: string) => {
      setEditedRefinementPrompt(value);
      setRefinementPromptChanged(value !== aiMockupSettings.settings?.refinementPromptTemplate);
      setRefinementPromptSaveSuccess(false);
    },
    [aiMockupSettings.settings?.refinementPromptTemplate]
  );

  // Save refinement prompt
  const handleSaveRefinementPrompt = useCallback(async () => {
    const success = await aiMockupSettings.updateRefinementPrompt(editedRefinementPrompt);
    if (success) {
      setRefinementPromptChanged(false);
      setRefinementPromptSaveSuccess(true);
      setTimeout(() => setRefinementPromptSaveSuccess(false), 3000);
    }
  }, [editedRefinementPrompt, aiMockupSettings]);

  // Handle patch template upload
  const handlePatchTemplateDrop = useCallback(
    async (_dropFiles: File[], acceptedFiles: File[]) => {
      const trimmedTemplateName = newTemplateName.trim();
      if (acceptedFiles.length > 0 && trimmedTemplateName) {
        const success = await patchTemplates.addTemplate(
          acceptedFiles[0],
          trimmedTemplateName,
          newTemplatePrompt.trim() || undefined
        );
        if (success) {
          setTemplateUploadSuccess(true);
          setNewTemplateName('');
          setNewTemplatePrompt('');
          setTimeout(() => setTemplateUploadSuccess(false), 3000);
        }
      }
    },
    [patchTemplates, newTemplateName, newTemplatePrompt]
  );

  // Handle template deletion
  const handleDeleteTemplate = useCallback(
    async (templateId: string) => {
      await patchTemplates.removeTemplate(templateId);
    },
    [patchTemplates]
  );

  // Handle opening edit modal
  const handleEditTemplate = useCallback((template: PatchTemplate) => {
    setEditingTemplate(template);
    setEditTemplateName(template.name);
    setEditTemplatePrompt(template.prompt || '');
    setTemplateEditSuccess(false);
  }, []);

  // Handle saving template edits
  const handleSaveTemplateEdit = useCallback(async () => {
    if (!editingTemplate) return;

    const updates: { name?: string; prompt?: string } = {};
    if (editTemplateName !== editingTemplate.name) {
      updates.name = editTemplateName;
    }
    if (editTemplatePrompt !== (editingTemplate.prompt || '')) {
      updates.prompt = editTemplatePrompt;
    }

    if (Object.keys(updates).length === 0) {
      setEditingTemplate(null);
      return;
    }

    const success = await patchTemplates.updateTemplate(editingTemplate.id, updates);
    if (success) {
      setTemplateEditSuccess(true);
      setTimeout(() => {
        setEditingTemplate(null);
        setTemplateEditSuccess(false);
      }, 1500);
    }
  }, [editingTemplate, editTemplateName, editTemplatePrompt, patchTemplates]);

  // Handle closing edit modal
  const handleCloseEditModal = useCallback(() => {
    setEditingTemplate(null);
    setTemplateEditSuccess(false);
  }, []);

  // Handle adding a sender pattern
  const handleAddPattern = useCallback(async () => {
    if (!newPatternEmail || !newPatternName) return;
    const success = await addPattern({
      email: newPatternEmail,
      name: newPatternName,
    });
    if (success) {
      setNewPatternEmail('');
      setNewPatternName('');
    }
  }, [newPatternEmail, newPatternName, addPattern]);

  // Handle setting production floor PIN
  const handleSetPin = useCallback(async () => {
    if (!/^\d{4}$/.test(newPin)) return;
    const success = await productionFloorConfig.setPin(newPin);
    if (success) {
      setNewPin('');
      setPinSaveSuccess(true);
      setTimeout(() => setPinSaveSuccess(false), 3000);
    }
  }, [newPin, productionFloorConfig]);

  // Handle toggling production floor enabled
  const handleToggleFloorEnabled = useCallback(async () => {
    const newValue = !productionFloorConfig.status?.isEnabled;
    await productionFloorConfig.setEnabled(newValue);
  }, [productionFloorConfig]);

  // Handle adding a thread color
  const handleAddColor = useCallback(async () => {
    if (!newColorName.trim() || !newColorHex) return;
    // Validate hex format
    if (!/^#[0-9A-Fa-f]{6}$/.test(newColorHex)) return;
    // Check for duplicate names
    if (threadColorsConfig.colors.some((c) => c.name.toLowerCase() === newColorName.trim().toLowerCase())) {
      return;
    }
    const success = await threadColorsConfig.addColor({
      name: newColorName.trim(),
      hex: newColorHex.toUpperCase(),
    });
    if (success) {
      setNewColorName('');
      setNewColorHex('#000000');
      setColorSaveSuccess(true);
      setTimeout(() => setColorSaveSuccess(false), 3000);
    }
  }, [newColorName, newColorHex, threadColorsConfig]);

  // Handle removing a thread color
  const handleRemoveColor = useCallback(
    async (colorName: string) => {
      await threadColorsConfig.removeColor(colorName);
    },
    [threadColorsConfig]
  );

  // Handle adding a QA checklist item
  const handleAddQAChecklistItem = useCallback(async () => {
    const trimmedLabel = newChecklistLabel.trim();
    if (!trimmedLabel) return;
    const success = await qaChecklistConfig.addItem({
      label: trimmedLabel,
      required: newChecklistRequired,
    });
    if (success) {
      setNewChecklistLabel('');
      setNewChecklistRequired(true);
      setQaChecklistSaveSuccess(true);
      setTimeout(() => setQaChecklistSaveSuccess(false), 3000);
    }
  }, [newChecklistLabel, newChecklistRequired, qaChecklistConfig]);

  // Handle removing a QA checklist item
  const handleRemoveQAChecklistItem = useCallback(
    async (itemId: string) => {
      await qaChecklistConfig.removeItem(itemId);
    },
    [qaChecklistConfig]
  );

  // Handle toggling QA checklist item required status
  const handleToggleQAChecklistRequired = useCallback(
    async (itemId: string, currentRequired: boolean) => {
      await qaChecklistConfig.updateItem(itemId, { required: !currentRequired });
    },
    [qaChecklistConfig]
  );

  // Handle toggling QA floor enabled
  const handleToggleQAFloorEnabled = useCallback(async () => {
    const newValue = !qaChecklistConfig.config?.isEnabled;
    await qaChecklistConfig.setEnabled(newValue);
  }, [qaChecklistConfig]);

  // Handle moving QA checklist item up
  const handleMoveQAChecklistItem = useCallback(
    async (itemId: string, direction: 'up' | 'down') => {
      if (!qaChecklistConfig.config) return;
      const items = [...qaChecklistConfig.config.checklistItems].sort((a, b) => a.order - b.order);
      const currentIndex = items.findIndex((i) => i.id === itemId);
      if (currentIndex === -1) return;

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= items.length) return;

      // Swap items
      [items[currentIndex], items[newIndex]] = [items[newIndex], items[currentIndex]];
      await qaChecklistConfig.reorderItems(items);
    },
    [qaChecklistConfig]
  );

  // Autocomplete options for product search
  const productOptions = useMemo(
    () =>
      filteredSearchResults.map((product) => ({
        value: product.id,
        label: product.title,
        media: product.featuredImage?.url ? (
          <Thumbnail
            source={product.featuredImage.url}
            alt={product.featuredImage.altText || product.title}
            size="small"
          />
        ) : undefined,
      })),
    [filteredSearchResults]
  );

  // Handle autocomplete selection
  const handleProductSelect = useCallback(
    (selected: string[]) => {
      const productId = selected[0];
      const product = searchResults.find((p) => p.id === productId);
      if (product) {
        handleAddProduct(product);
      }
    },
    [searchResults, handleAddProduct]
  );

  return (
    <Page
      title="Settings"
      backAction={{ content: 'Dashboard', onAction: () => navigate('/') }}
    >
      <Layout>
        {/* Tracked Products */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Tracked Products
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Orders containing these products will automatically enter the production pipeline.
              </Text>

              {productsError && (
                <Banner tone="critical">
                  <p>{productsError}</p>
                </Banner>
              )}

              <Divider />

              {productsLoading ? (
                <SkeletonBodyText lines={3} />
              ) : productsArray.length === 0 ? (
                <Text as="p" tone="subdued">
                  No products are being tracked yet. Search for products below to add them.
                </Text>
              ) : (
                <BlockStack gap="300">
                  {productsArray.map((product) => (
                    <InlineStack key={product.shopifyProductId} align="space-between" blockAlign="center" gap="300">
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <StoreBadge currency={product.priceRange?.currencyCode} />
                        <Text as="span" variant="bodyMd">
                          {product.shopifyProductTitle}
                        </Text>
                      </div>
                      <InlineStack gap="200" blockAlign="center">
                        {product.priceRange && (
                          <Text as="span" variant="bodySm" tone="subdued">
                            {product.priceRange.min === product.priceRange.max
                              ? `$${parseFloat(product.priceRange.min).toFixed(2)}`
                              : `$${parseFloat(product.priceRange.min).toFixed(2)} – $${parseFloat(product.priceRange.max).toFixed(2)}`}
                          </Text>
                        )}
                        <div style={{ width: '150px' }}>
                          <Select
                            label="Type"
                            labelHidden
                            options={[
                              { label: 'Embroidery', value: 'embroidery' },
                              { label: 'Patch', value: 'patch' },
                              { label: 'Both', value: 'both' },
                              { label: 'Undetermined', value: 'undetermined' },
                            ]}
                            value={product.productType}
                            onChange={(value) => handleProductTypeChange(product.shopifyProductId, value as ProductType)}
                            disabled={productActionLoading}
                          />
                        </div>
                        <Button
                          size="slim"
                          tone="critical"
                          variant="plain"
                          onClick={() => removeProduct(product.shopifyProductId)}
                          loading={productActionLoading}
                        >
                          Remove
                        </Button>
                      </InlineStack>
                    </InlineStack>
                  ))}
                </BlockStack>
              )}

              <Divider />

              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  Add Product
                </Text>
                <InlineStack gap="300" blockAlign="end">
                  <div style={{ flex: 1 }}>
                    <Autocomplete
                      options={productOptions}
                      selected={[]}
                      onSelect={handleProductSelect}
                      textField={
                        <Autocomplete.TextField
                          label="Search products"
                          labelHidden
                          value={productSearchValue}
                          onChange={handleProductSearchChange}
                          placeholder="Search for a product to track..."
                          autoComplete="off"
                          prefix={<Icon source={SearchIcon} />}
                          suffix={searchLoading ? <Spinner size="small" /> : null}
                        />
                      }
                    />
                  </div>
                  <div style={{ width: '160px' }}>
                    <Select
                      label="Product Type"
                      labelHidden
                      options={[
                        { label: 'Embroidery', value: 'embroidery' },
                        { label: 'Patch', value: 'patch' },
                        { label: 'Both', value: 'both' },
                        { label: 'Undetermined', value: 'undetermined' },
                      ]}
                      value={selectedProductType}
                      onChange={(value) => setSelectedProductType(value as ProductType)}
                    />
                  </div>
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">
                  Select product type to determine workflow: Embroidery uses full digitization workflow, Patch skips to proof creation.
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Size Selection Collections */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Size Selection Collections
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Select which Shopify collections appear on the customer size selection page
                (shown after mockup approval for draft orders). All products in these collections
                will be available for customers to choose sizes from.
              </Text>

              {sizeSelectionCollections.error && (
                <Banner tone="critical">
                  <p>{sizeSelectionCollections.error}</p>
                </Banner>
              )}

              {sizeSelectionSaveSuccess && (
                <Banner tone="success">
                  <p>Size selection collections updated!</p>
                </Banner>
              )}

              <Divider />

              {sizeSelectionCollections.loading ? (
                <SkeletonBodyText lines={3} />
              ) : (
                <BlockStack gap="300">
                  {/* Current collections */}
                  {sizeSelectionCollections.collections.length === 0 ? (
                    <Text as="p" tone="subdued">
                      No collections configured yet. Search below to add collections.
                    </Text>
                  ) : (
                    <BlockStack gap="200">
                      {sizeSelectionCollections.collections.map((col) => (
                        <InlineStack key={col.id} align="space-between" blockAlign="center">
                          <Text as="span" variant="bodyMd">
                            {col.title}
                          </Text>
                          <Button
                            size="slim"
                            tone="critical"
                            variant="plain"
                            onClick={() => handleRemoveSizeSelectionCollection(col.id)}
                            loading={sizeSelectionCollections.actionLoading}
                          >
                            Remove
                          </Button>
                        </InlineStack>
                      ))}
                    </BlockStack>
                  )}

                  <Divider />

                  {/* Add collection search */}
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">
                      Add Collection
                    </Text>
                    <Autocomplete
                      options={sizeSelectionCollections.searchResults
                        .filter((c) => !sizeSelectionCollections.collections.some((sel) => sel.id === c.id))
                        .map((c) => ({ value: c.id, label: c.title }))}
                      selected={[]}
                      onSelect={handleAddSizeSelectionCollection}
                      textField={
                        <Autocomplete.TextField
                          label="Search collections"
                          labelHidden
                          value={collectionSearchValue}
                          onChange={handleCollectionSearchChange}
                          placeholder="Search for a collection..."
                          autoComplete="off"
                          prefix={<Icon source={SearchIcon} />}
                          suffix={sizeSelectionCollections.searchLoading ? <Spinner size="small" /> : null}
                        />
                      }
                    />
                  </BlockStack>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Patch Products Collection */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Patch Products Collection
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Select which Shopify collection contains your patch products. When setting an
                order's product type to "Patch" on a draft order, only products from this collection
                will appear in the search.
              </Text>

              {patchCollectionConfig.error && (
                <Banner tone="critical">
                  <p>{patchCollectionConfig.error}</p>
                </Banner>
              )}

              {patchCollectionSaveSuccess && (
                <Banner tone="success">
                  <p>Patch collection updated!</p>
                </Banner>
              )}

              <Divider />

              {patchCollectionConfig.loading ? (
                <SkeletonBodyText lines={2} />
              ) : (
                <BlockStack gap="300">
                  {patchCollectionConfig.collection ? (
                    <InlineStack align="space-between" blockAlign="center">
                      <BlockStack gap="050">
                        <Text as="span" variant="bodyMd" fontWeight="semibold">
                          {patchCollectionConfig.collection.title}
                        </Text>
                        <Text as="span" variant="bodySm" tone="subdued">
                          Handle: {patchCollectionConfig.collection.handle}
                        </Text>
                      </BlockStack>
                      <Button
                        size="slim"
                        tone="critical"
                        variant="plain"
                        onClick={handleRemovePatchCollection}
                        loading={patchCollectionConfig.actionLoading}
                      >
                        Remove
                      </Button>
                    </InlineStack>
                  ) : (
                    <Text as="p" tone="subdued">
                      No collection configured. Search below to set one.
                    </Text>
                  )}

                  <Divider />

                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">
                      {patchCollectionConfig.collection ? 'Change Collection' : 'Set Collection'}
                    </Text>
                    <Autocomplete
                      options={patchCollectionSearchResults
                        .map((c) => ({ value: c.id, label: c.title }))}
                      selected={[]}
                      onSelect={handleSelectPatchCollection}
                      textField={
                        <Autocomplete.TextField
                          label="Search collections"
                          labelHidden
                          value={patchCollectionSearchValue}
                          onChange={handlePatchCollectionSearchChange}
                          placeholder="Search for a collection..."
                          autoComplete="off"
                          prefix={<Icon source={SearchIcon} />}
                          suffix={patchCollectionSearchLoading ? <Spinner size="small" /> : null}
                        />
                      }
                    />
                  </BlockStack>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Gmail Integration */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  Gmail Integration
                </Text>
                {gmailLoading ? (
                  <Spinner size="small" />
                ) : gmailStatus?.isConnected ? (
                  <Badge tone="success">Connected</Badge>
                ) : (
                  <Badge tone="attention">Not Connected</Badge>
                )}
              </InlineStack>

              {gmailError && (
                <Banner tone="critical">
                  <p>{gmailError}</p>
                </Banner>
              )}

              {gmailStatus?.isConnected ? (
                <>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Connected as: {gmailStatus.email}
                  </Text>
                  {gmailStatus.connectedAt && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      Connected: {new Date(gmailStatus.connectedAt).toLocaleDateString()}
                    </Text>
                  )}

                  <Divider />

                  {/* Email Sender Patterns */}
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">
                      Trusted Vendor Email Patterns
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Emails matching these patterns will be processed for digitization files.
                      Use * as a wildcard (e.g., *@vendor.com)
                    </Text>

                    {patternsError && (
                      <Banner tone="warning">
                        <p>{patternsError}</p>
                      </Banner>
                    )}

                    {patternsLoading ? (
                      <SkeletonBodyText lines={2} />
                    ) : senderPatterns.length === 0 ? (
                      <Text as="p" tone="subdued">
                        No email patterns configured yet.
                      </Text>
                    ) : (
                      <InlineStack gap="200" wrap>
                        {senderPatterns.map((pattern) => (
                          <Tag
                            key={pattern.id}
                            onRemove={() => removePattern(pattern.id)}
                          >
                            {pattern.name}: {pattern.email}
                          </Tag>
                        ))}
                      </InlineStack>
                    )}

                    <InlineStack gap="200" blockAlign="end">
                      <div style={{ flex: 1 }}>
                        <TextField
                          label="Vendor Name"
                          value={newPatternName}
                          onChange={setNewPatternName}
                          placeholder="e.g., Embroidery Co"
                          autoComplete="off"
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <TextField
                          label="Email Pattern"
                          value={newPatternEmail}
                          onChange={setNewPatternEmail}
                          placeholder="e.g., *@vendor.com"
                          autoComplete="off"
                        />
                      </div>
                      <Button
                        onClick={handleAddPattern}
                        loading={patternActionLoading}
                        disabled={!newPatternEmail || !newPatternName}
                      >
                        Add
                      </Button>
                    </InlineStack>
                  </BlockStack>

                  <Divider />

                  <Button
                    tone="critical"
                    onClick={disconnectGmail}
                    loading={gmailActionLoading}
                  >
                    Disconnect Gmail
                  </Button>
                </>
              ) : (
                <>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Connect your Gmail account to automatically import digitization files
                    from vendor emails.
                  </Text>
                  <Button
                    variant="primary"
                    onClick={connectGmail}
                    loading={gmailActionLoading}
                  >
                    Connect Gmail
                  </Button>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Gmail Accounts (Multi-Account Communication) */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  Gmail Accounts
                </Text>
                {gmailAccountsLoading ? (
                  <Spinner size="small" />
                ) : (
                  <Badge>{String(gmailAccounts.length)}</Badge>
                )}
              </InlineStack>

              <Text as="p" variant="bodySm" tone="subdued">
                Connect staff Gmail accounts to sync customer communications. These are separate from the vendor file import above.
              </Text>

              {gmailAccountsError && (
                <Banner tone="critical">
                  <p>{gmailAccountsError}</p>
                </Banner>
              )}

              {gmailAccounts.length > 0 && (
                <BlockStack gap="200">
                  {gmailAccounts.map((account) => (
                    <InlineStack key={account.id} align="space-between" blockAlign="center">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="span" variant="bodySm" fontWeight="semibold">
                          {account.email}
                        </Text>
                        <Badge tone="info">{account.label}</Badge>
                        <Badge tone={account.isActive ? 'success' : undefined}>
                          {account.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </InlineStack>
                      <Button
                        variant="plain"
                        tone="critical"
                        onClick={() => disconnectAccount(account.id)}
                        loading={gmailAccountsActionLoading}
                      >
                        Disconnect
                      </Button>
                    </InlineStack>
                  ))}
                </BlockStack>
              )}

              <Button onClick={() => setConnectModalOpen(true)}>
                Connect Gmail Account
              </Button>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* AI Mockup Generation Settings */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  AI Mockup Generation
                </Text>
                <Badge tone="info">Gemini AI</Badge>
              </InlineStack>
              <Text as="p" variant="bodySm" tone="subdued">
                Configure AI-powered mockup generation for product proofs.
              </Text>

              {(aiMockupSettings.error || patchTemplates.error) && (
                <Banner tone="critical">
                  <p>{aiMockupSettings.error || patchTemplates.error}</p>
                </Banner>
              )}

              <Divider />

              {aiMockupSettings.loading || patchTemplates.loading ? (
                <SkeletonBodyText lines={5} />
              ) : (
                <>
                  {/* Patch Templates */}
                  <BlockStack gap="300">
                    <Text as="h3" variant="headingSm">
                      Patch Templates
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Manage your patch templates. Customer logos will be composited onto these before
                      being placed on product images. Designers can select which template to use for
                      each mockup.
                    </Text>

                    {templateUploadSuccess && (
                      <Banner tone="success">
                        <p>Template added successfully!</p>
                      </Banner>
                    )}

                    {/* Template List */}
                    {patchTemplates.templates.length === 0 ? (
                      <Text as="p" tone="subdued">
                        No patch templates uploaded yet. Add your first template below.
                      </Text>
                    ) : (
                      <BlockStack gap="300">
                        {patchTemplates.templates.map((template) => (
                          <InlineStack key={template.id} gap="400" blockAlign="center">
                            <div
                              style={{
                                border: '1px solid #e1e3e5',
                                borderRadius: '8px',
                                padding: '8px',
                                backgroundColor: '#f4f6f8',
                                minWidth: '80px',
                                textAlign: 'center',
                              }}
                            >
                              {template.previewUrl ? (
                                <img
                                  src={template.previewUrl}
                                  alt={template.name}
                                  style={{ maxHeight: '60px', objectFit: 'contain' }}
                                />
                              ) : (
                                <Text as="span" tone="subdued">
                                  No preview
                                </Text>
                              )}
                            </div>
                            <BlockStack gap="100">
                              <Text as="span" variant="bodyMd" fontWeight="semibold">
                                {template.name}
                              </Text>
                              <Text as="span" variant="bodySm" tone="subdued">
                                {template.prompt
                                  ? `${template.prompt.substring(0, 50)}...`
                                  : 'Using default prompt'}
                              </Text>
                              <Text as="span" variant="bodySm" tone="subdued">
                                Added {new Date(template.createdAt).toLocaleDateString()}
                              </Text>
                            </BlockStack>
                            <div style={{ marginLeft: 'auto' }}>
                              <InlineStack gap="200">
                                <Button
                                  icon={EditIcon}
                                  variant="plain"
                                  onClick={() => handleEditTemplate(template)}
                                  disabled={patchTemplates.actionLoading}
                                />
                                <Button
                                  icon={DeleteIcon}
                                  tone="critical"
                                  variant="plain"
                                  onClick={() => handleDeleteTemplate(template.id)}
                                  loading={patchTemplates.actionLoading}
                                />
                              </InlineStack>
                            </div>
                          </InlineStack>
                        ))}
                      </BlockStack>
                    )}

                    {/* Add New Template */}
                    <Divider />
                    <Text as="h4" variant="headingSm">
                      Add New Template
                    </Text>
                    <TextField
                      label="Template Name"
                      value={newTemplateName}
                      onChange={setNewTemplateName}
                      placeholder="e.g., Round Embroidered Patch, Rubber PVC Patch, etc."
                      autoComplete="off"
                    />
                    <TextField
                      label="Stage 1 Prompt (Logo on Patch)"
                      value={newTemplatePrompt}
                      onChange={setNewTemplatePrompt}
                      multiline={3}
                      placeholder="e.g., Take this logo and place it on this embroidered patch. The logo should appear as if it was stitched with thread..."
                      autoComplete="off"
                      helpText="This prompt tells AI how to place the customer's logo onto this patch template. Leave blank to use default."
                    />
                    <DropZone
                      onDrop={handlePatchTemplateDrop}
                      accept="image/*"
                      type="image"
                      allowMultiple={false}
                      disabled={!newTemplateName.trim()}
                    >
                      <DropZone.FileUpload
                        actionHint={
                          newTemplateName.trim()
                            ? 'Drop patch template image or click to upload'
                            : 'Enter a template name first'
                        }
                      />
                    </DropZone>
                  </BlockStack>

                  <Divider />

                  {/* Stage 2 Refinement Prompt */}
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">
                      Stage 2: Refinement Prompt (Patch on Product)
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      This global prompt is used in Stage 2 to place the generated patch onto the product image.
                      It applies to all mockups regardless of which patch template is used.
                    </Text>

                    {refinementPromptSaveSuccess && (
                      <Banner tone="success">
                        <p>Refinement prompt saved successfully!</p>
                      </Banner>
                    )}

                    <TextField
                      label="Refinement Prompt"
                      labelHidden
                      value={editedRefinementPrompt}
                      onChange={handleRefinementPromptChange}
                      multiline={5}
                      autoComplete="off"
                      placeholder="Place the patch from the first image onto the product in the second image. The patch should appear naturally attached to the front of the product..."
                    />

                    <InlineStack gap="200">
                      <Button
                        variant="primary"
                        onClick={handleSaveRefinementPrompt}
                        loading={aiMockupSettings.actionLoading}
                        disabled={!refinementPromptChanged || aiMockupSettings.actionLoading}
                      >
                        Save Refinement Prompt
                      </Button>
                      {refinementPromptChanged && (
                        <Text as="span" variant="bodySm" tone="subdued">
                          Unsaved changes
                        </Text>
                      )}
                    </InlineStack>
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Production Floor Settings */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  Production Floor Access
                </Text>
                {productionFloorConfig.loading ? (
                  <Spinner size="small" />
                ) : productionFloorConfig.status?.isEnabled ? (
                  <Badge tone="success">Enabled</Badge>
                ) : (
                  <Badge tone="attention">Disabled</Badge>
                )}
              </InlineStack>
              <Text as="p" variant="bodySm" tone="subdued">
                Configure PIN-based access for production floor workers. Workers can access
                the floor interface at <strong>/floor/</strong> to view orders and record
                completion data.
              </Text>

              {productionFloorConfig.error && (
                <Banner tone="critical">
                  <p>{productionFloorConfig.error}</p>
                </Banner>
              )}

              {pinSaveSuccess && (
                <Banner tone="success">
                  <p>PIN updated successfully!</p>
                </Banner>
              )}

              <Divider />

              {productionFloorConfig.loading ? (
                <SkeletonBodyText lines={3} />
              ) : (
                <>
                  {/* PIN Configuration */}
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">
                      {productionFloorConfig.status?.isConfigured ? 'Update PIN' : 'Set PIN'}
                    </Text>
                    <InlineStack gap="200" blockAlign="end">
                      <div style={{ width: '150px' }}>
                        <TextField
                          label="4-digit PIN"
                          labelHidden
                          type="password"
                          inputMode="numeric"
                          maxLength={4}
                          value={newPin}
                          onChange={setNewPin}
                          placeholder="----"
                          autoComplete="off"
                        />
                      </div>
                      <Button
                        onClick={handleSetPin}
                        loading={productionFloorConfig.actionLoading}
                        disabled={!/^\d{4}$/.test(newPin)}
                      >
                        {productionFloorConfig.status?.isConfigured ? 'Update PIN' : 'Set PIN'}
                      </Button>
                    </InlineStack>
                    {productionFloorConfig.status?.isConfigured && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        PIN is configured. Enter a new 4-digit PIN to change it.
                      </Text>
                    )}
                    {productionFloorConfig.status?.lastUpdatedAt && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        Last updated: {new Date(productionFloorConfig.status.lastUpdatedAt).toLocaleDateString()}
                      </Text>
                    )}
                  </BlockStack>

                  <Divider />

                  {/* Enable/Disable */}
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <Text as="span" variant="bodyMd">
                        Production Floor Access
                      </Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {productionFloorConfig.status?.isEnabled
                          ? 'Workers can log in using the PIN'
                          : 'Access is currently disabled'}
                      </Text>
                    </BlockStack>
                    <Button
                      onClick={handleToggleFloorEnabled}
                      loading={productionFloorConfig.actionLoading}
                      disabled={!productionFloorConfig.status?.isConfigured}
                      tone={productionFloorConfig.status?.isEnabled ? 'critical' : undefined}
                    >
                      {productionFloorConfig.status?.isEnabled ? 'Disable Access' : 'Enable Access'}
                    </Button>
                  </InlineStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Thread Colors Configuration */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Thread Colors
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Configure the available thread colors for the production floor. Workers will select
                from these colors when recording production details.
              </Text>

              {threadColorsConfig.error && (
                <Banner tone="critical">
                  <p>{threadColorsConfig.error}</p>
                </Banner>
              )}

              {colorSaveSuccess && (
                <Banner tone="success">
                  <p>Color added successfully!</p>
                </Banner>
              )}

              <Divider />

              {threadColorsConfig.loading ? (
                <SkeletonBodyText lines={3} />
              ) : (
                <>
                  {/* Color List */}
                  {threadColorsConfig.colors.length === 0 ? (
                    <Text as="p" tone="subdued">
                      No thread colors configured. Add colors below.
                    </Text>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {threadColorsConfig.colors.map((color) => (
                        <Tag
                          key={color.name}
                          onRemove={() => handleRemoveColor(color.name)}
                        >
                          <InlineStack gap="200" blockAlign="center">
                            <div
                              style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: color.hex,
                                border: '1px solid #ccc',
                              }}
                            />
                            <span>{color.name}</span>
                          </InlineStack>
                        </Tag>
                      ))}
                    </div>
                  )}

                  <Divider />

                  {/* Add New Color */}
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">
                      Add Color
                    </Text>
                    <InlineStack gap="200" blockAlign="end">
                      <div style={{ width: '180px' }}>
                        <TextField
                          label="Color Name"
                          value={newColorName}
                          onChange={setNewColorName}
                          placeholder="e.g., Navy Blue"
                          autoComplete="off"
                        />
                      </div>
                      <div style={{ width: '120px' }}>
                        <TextField
                          label="Hex Color"
                          value={newColorHex}
                          onChange={setNewColorHex}
                          placeholder="#000000"
                          autoComplete="off"
                          prefix={
                            <div
                              style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '4px',
                                backgroundColor: newColorHex,
                                border: '1px solid #ccc',
                              }}
                            />
                          }
                        />
                      </div>
                      <div style={{ width: '60px' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: 'transparent' }}>Pick</label>
                        <input
                          type="color"
                          value={newColorHex}
                          onChange={(e) => setNewColorHex(e.target.value.toUpperCase())}
                          style={{
                            width: '40px',
                            height: '36px',
                            border: 'none',
                            cursor: 'pointer',
                            borderRadius: '4px',
                          }}
                        />
                      </div>
                      <Button
                        onClick={handleAddColor}
                        loading={threadColorsConfig.actionLoading}
                        disabled={!newColorName.trim() || !/^#[0-9A-Fa-f]{6}$/i.test(newColorHex)}
                      >
                        Add
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* QA Checklist Configuration */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  QA Checklist
                </Text>
                {qaChecklistConfig.loading ? (
                  <Spinner size="small" />
                ) : qaChecklistConfig.config?.isEnabled ? (
                  <Badge tone="success">Enabled</Badge>
                ) : (
                  <Badge tone="attention">Disabled</Badge>
                )}
              </InlineStack>
              <Text as="p" variant="bodySm" tone="subdued">
                Configure the checklist items that QA workers must complete before marking
                an order as ready to ship. Workers access the QA interface at <strong>/qa/</strong>.
              </Text>

              {qaChecklistConfig.error && (
                <Banner tone="critical">
                  <p>{qaChecklistConfig.error}</p>
                </Banner>
              )}

              {qaChecklistSaveSuccess && (
                <Banner tone="success">
                  <p>Checklist item added successfully!</p>
                </Banner>
              )}

              <Divider />

              {qaChecklistConfig.loading ? (
                <SkeletonBodyText lines={3} />
              ) : (
                <>
                  {/* Checklist Items */}
                  {qaChecklistConfig.config?.checklistItems.length === 0 ? (
                    <Text as="p" tone="subdued">
                      No checklist items configured. Add items below.
                    </Text>
                  ) : (
                    <BlockStack gap="200">
                      {[...(qaChecklistConfig.config?.checklistItems || [])]
                        .sort((a, b) => a.order - b.order)
                        .map((item, index, arr) => (
                          <InlineStack key={item.id} align="space-between" blockAlign="center">
                            <InlineStack gap="300" blockAlign="center">
                              <BlockStack gap="100">
                                <Button
                                  size="slim"
                                  variant="plain"
                                  disabled={index === 0 || qaChecklistConfig.actionLoading}
                                  onClick={() => handleMoveQAChecklistItem(item.id, 'up')}
                                >
                                  ↑
                                </Button>
                                <Button
                                  size="slim"
                                  variant="plain"
                                  disabled={index === arr.length - 1 || qaChecklistConfig.actionLoading}
                                  onClick={() => handleMoveQAChecklistItem(item.id, 'down')}
                                >
                                  ↓
                                </Button>
                              </BlockStack>
                              <Text as="span" variant="bodyMd">
                                {item.label}
                              </Text>
                              <Button
                                size="slim"
                                variant="plain"
                                tone={item.required ? 'critical' : undefined}
                                onClick={() => handleToggleQAChecklistRequired(item.id, item.required)}
                                loading={qaChecklistConfig.actionLoading}
                              >
                                {item.required ? 'Required' : 'Optional'}
                              </Button>
                            </InlineStack>
                            <Button
                              size="slim"
                              tone="critical"
                              variant="plain"
                              onClick={() => handleRemoveQAChecklistItem(item.id)}
                              loading={qaChecklistConfig.actionLoading}
                            >
                              Remove
                            </Button>
                          </InlineStack>
                        ))}
                    </BlockStack>
                  )}

                  <Divider />

                  {/* Add New Item */}
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">
                      Add Checklist Item
                    </Text>
                    <InlineStack gap="200" blockAlign="end">
                      <div style={{ flex: 1 }}>
                        <TextField
                          label="Item Label"
                          value={newChecklistLabel}
                          onChange={setNewChecklistLabel}
                          placeholder="e.g., Thread colors match order"
                          autoComplete="off"
                        />
                      </div>
                      <div style={{ width: '140px' }}>
                        <Select
                          label="Required"
                          options={[
                            { label: 'Required', value: 'true' },
                            { label: 'Optional', value: 'false' },
                          ]}
                          value={newChecklistRequired ? 'true' : 'false'}
                          onChange={(value) => setNewChecklistRequired(value === 'true')}
                        />
                      </div>
                      <Button
                        onClick={handleAddQAChecklistItem}
                        loading={qaChecklistConfig.actionLoading}
                        disabled={!newChecklistLabel.trim()}
                      >
                        Add
                      </Button>
                    </InlineStack>
                  </BlockStack>

                  <Divider />

                  {/* Enable/Disable QA Floor */}
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <Text as="span" variant="bodyMd">
                        QA Floor Access
                      </Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {qaChecklistConfig.config?.isEnabled
                          ? 'Workers can log in using the production floor PIN'
                          : 'Access is currently disabled'}
                      </Text>
                    </BlockStack>
                    <Button
                      onClick={handleToggleQAFloorEnabled}
                      loading={qaChecklistConfig.actionLoading}
                      tone={qaChecklistConfig.config?.isEnabled ? 'critical' : undefined}
                    >
                      {qaChecklistConfig.config?.isEnabled ? 'Disable Access' : 'Enable Access'}
                    </Button>
                  </InlineStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Email Templates Info */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Email Templates
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Email templates are managed in SendGrid. Use the links below to edit templates.
              </Text>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="span" variant="bodyMd">
                    Proof Approval Request
                  </Text>
                  <Button
                    variant="plain"
                    external
                    url="https://app.sendgrid.com/dynamic_template_id_here"
                  >
                    Edit in SendGrid
                  </Button>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" variant="bodyMd">
                    Approval Confirmation
                  </Text>
                  <Button
                    variant="plain"
                    external
                    url="https://app.sendgrid.com/dynamic_template_id_here"
                  >
                    Edit in SendGrid
                  </Button>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Shopify Webhooks */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  Shopify Webhooks
                </Text>
                {webhookStatus.loading ? (
                  <Spinner size="small" />
                ) : webhookStatus.allRequired ? (
                  <Badge tone="success">All Connected</Badge>
                ) : (
                  <Badge tone="warning">Setup Required</Badge>
                )}
              </InlineStack>
              <Text as="p" variant="bodySm" tone="subdued">
                Webhooks allow the app to automatically receive updates from Shopify, including when orders
                are fulfilled (shipped). This enables automatic status updates when you mark orders as shipped in Shopify.
              </Text>

              {webhookStatus.error && (
                <Banner tone="critical">
                  <p>{webhookStatus.error}</p>
                </Banner>
              )}

              {webhookSuccess && (
                <Banner tone="success">
                  <p>Webhooks registered successfully!</p>
                </Banner>
              )}

              <Divider />

              {webhookStatus.loading ? (
                <SkeletonBodyText lines={3} />
              ) : (
                <BlockStack gap="300">
                  {webhookStatus.requiredWebhooks.map((webhook) => (
                    <InlineStack key={webhook.topic} align="space-between" blockAlign="center">
                      <BlockStack gap="100">
                        <Text as="span" variant="bodyMd">
                          {webhook.topic.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
                        </Text>
                        <Text as="span" variant="bodySm" tone="subdued">
                          {webhook.topic === 'ORDERS_CREATE' && 'New orders are automatically tracked'}
                          {webhook.topic === 'ORDERS_UPDATED' && 'Order changes sync to the app'}
                          {webhook.topic === 'FULFILLMENTS_CREATE' && 'Orders auto-transition to Shipped when fulfilled'}
                        </Text>
                      </BlockStack>
                      {webhook.isRegistered ? (
                        <Badge tone="success">Connected</Badge>
                      ) : (
                        <Badge tone="attention">Not Connected</Badge>
                      )}
                    </InlineStack>
                  ))}

                  {!webhookStatus.allRequired && (
                    <>
                      <Divider />
                      <InlineStack align="end">
                        <Button
                          variant="primary"
                          onClick={handleRegisterWebhooks}
                          loading={webhookRegistering}
                        >
                          Register Webhooks
                        </Button>
                      </InlineStack>
                    </>
                  )}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>


        {/* Automatic Follow-Ups */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  Automatic Follow-Ups
                </Text>
                {followUpConfig && (
                  <Button
                    variant={followUpConfig.enabled ? 'primary' : undefined}
                    onClick={() => saveFollowUpConfig({ enabled: !followUpConfig.enabled })}
                    loading={followUpActionLoading}
                    size="slim"
                  >
                    {followUpConfig.enabled ? 'Enabled' : 'Disabled'}
                  </Button>
                )}
              </InlineStack>

              <Text as="p" variant="bodySm" tone="subdued">
                Automatically send email or SMS notifications when orders enter stages or sit too long.
              </Text>

              {followUpError && (
                <Banner tone="critical">
                  <p>{followUpError}</p>
                </Banner>
              )}

              {!followUpConfig?.enabled && followUpConfig && (
                <Banner tone="warning">
                  <p>Follow-up notifications are currently disabled. Enable them above to start sending.</p>
                </Banner>
              )}

              {followUpLoading ? (
                <SkeletonBodyText lines={4} />
              ) : followUpConfig ? (
                <>
                  {/* Staff Contact Info */}
                  <Divider />
                  <Text as="h3" variant="headingSm">Staff Notification Contacts</Text>
                  <InlineStack gap="400" wrap={false}>
                    <div style={{ flex: 1 }}>
                      <TextField
                        label="Staff Email"
                        value={followUpStaffEmail}
                        onChange={setFollowUpStaffEmail}
                        autoComplete="email"
                        placeholder="brendon@sixhats.ca"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <TextField
                        label="Staff Phone (E.164)"
                        value={followUpStaffPhone}
                        onChange={setFollowUpStaffPhone}
                        autoComplete="tel"
                        placeholder="+1XXXXXXXXXX"
                      />
                    </div>
                  </InlineStack>
                  {(followUpStaffEmail !== (followUpConfig.staffEmail || '') ||
                    followUpStaffPhone !== (followUpConfig.staffPhone || '')) && (
                    <Button
                      onClick={handleSaveStaffContacts}
                      loading={followUpActionLoading}
                      size="slim"
                    >
                      Save Contacts
                    </Button>
                  )}

                  {/* Immediate Rules */}
                  <Divider />
                  <Text as="h3" variant="headingSm">Immediate Notifications</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Sent instantly when an order enters a stage.
                  </Text>
                  <BlockStack gap="200">
                    {followUpConfig.rules
                      .filter((r) => r.timing === 'immediate')
                      .map((rule) => (
                        <InlineStack key={rule.id} align="space-between" blockAlign="center" gap="200">
                          <InlineStack gap="200" blockAlign="center">
                            <Badge tone={rule.enabled ? 'success' : undefined}>
                              {rule.enabled ? 'On' : 'Off'}
                            </Badge>
                            <Badge tone={STAGE_TONES_MAP[rule.stage]}>
                              {STAGE_LABELS[rule.stage]}
                            </Badge>
                            <Tag>{rule.audience === 'customer' ? 'Customer' : 'Staff'}</Tag>
                            <Tag>{rule.channel === 'email' ? 'Email' : 'SMS'}</Tag>
                          </InlineStack>
                          <InlineStack gap="200">
                            <Button
                              size="slim"
                              onClick={() => toggleFollowUpRule(rule.id, !rule.enabled)}
                              loading={followUpActionLoading}
                            >
                              {rule.enabled ? 'Disable' : 'Enable'}
                            </Button>
                            <Button
                              size="slim"
                              onClick={() => handleOpenEditRule(rule)}
                              icon={EditIcon}
                            />
                          </InlineStack>
                        </InlineStack>
                      ))}
                  </BlockStack>

                  {/* Reminder Rules */}
                  <Divider />
                  <Text as="h3" variant="headingSm">Reminder Notifications</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Sent after an order sits in a stage for too long.
                  </Text>
                  <BlockStack gap="200">
                    {followUpConfig.rules
                      .filter((r) => r.timing === 'reminder')
                      .map((rule) => (
                        <InlineStack key={rule.id} align="space-between" blockAlign="center" gap="200">
                          <InlineStack gap="200" blockAlign="center" wrap>
                            <Badge tone={rule.enabled ? 'success' : undefined}>
                              {rule.enabled ? 'On' : 'Off'}
                            </Badge>
                            <Badge tone={STAGE_TONES_MAP[rule.stage]}>
                              {STAGE_LABELS[rule.stage]}
                            </Badge>
                            <Tag>{rule.audience === 'customer' ? 'Customer' : 'Staff'}</Tag>
                            <Tag>{rule.channel === 'email' ? 'Email' : 'SMS'}</Tag>
                            {rule.delayHours && (
                              <Text as="span" variant="bodySm" tone="subdued">
                                after {rule.delayHours}h
                              </Text>
                            )}
                          </InlineStack>
                          <InlineStack gap="200">
                            <Button
                              size="slim"
                              onClick={() => toggleFollowUpRule(rule.id, !rule.enabled)}
                              loading={followUpActionLoading}
                            >
                              {rule.enabled ? 'Disable' : 'Enable'}
                            </Button>
                            <Button
                              size="slim"
                              onClick={() => handleOpenEditRule(rule)}
                              icon={EditIcon}
                            />
                          </InlineStack>
                        </InlineStack>
                      ))}
                  </BlockStack>

                  {/* Activity Log */}
                  <Divider />
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h3" variant="headingSm">Recent Activity</Text>
                    <Button
                      size="slim"
                      onClick={() => {
                        setShowFollowUpActivity(!showFollowUpActivity);
                        if (!showFollowUpActivity) refetchActivity();
                      }}
                    >
                      {showFollowUpActivity ? 'Hide' : 'Show'}
                    </Button>
                  </InlineStack>

                  {showFollowUpActivity && (
                    <BlockStack gap="200">
                      {activityLoading && followUpActivity.length === 0 ? (
                        <SkeletonBodyText lines={3} />
                      ) : followUpActivity.length === 0 ? (
                        <Text as="p" variant="bodySm" tone="subdued">
                          No follow-ups sent yet.
                        </Text>
                      ) : (
                        <>
                          {followUpActivity.map((item) => (
                            <InlineStack key={item.id} gap="200" blockAlign="center" wrap>
                              <Badge tone={item.success ? 'success' : 'critical'}>
                                {item.success ? 'Sent' : 'Failed'}
                              </Badge>
                              <Text as="span" variant="bodySm">
                                {item.orderName}
                              </Text>
                              <Tag>{item.channel}</Tag>
                              <Tag>{item.audience}</Tag>
                              <Text as="span" variant="bodySm" tone="subdued">
                                {STAGE_LABELS[item.stage] || item.stage}
                              </Text>
                              <Text as="span" variant="bodySm" tone="subdued">
                                {new Date(item.sentAt).toLocaleString()}
                              </Text>
                              {item.error && (
                                <Text as="span" variant="bodySm" tone="critical">
                                  {item.error}
                                </Text>
                              )}
                            </InlineStack>
                          ))}
                          {activityHasMore && (
                            <Button size="slim" onClick={loadMoreActivity} loading={activityLoading}>
                              Load more
                            </Button>
                          )}
                        </>
                      )}
                    </BlockStack>
                  )}
                </>
              ) : null}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* About */}
        <Layout.Section>
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">
                About
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Six Hats Production Pipeline v1.0.0
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Manage custom orders through digitization, proofing, and production.
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Edit Template Modal */}
      <Modal
        open={!!editingTemplate}
        onClose={handleCloseEditModal}
        title={`Edit Template: ${editingTemplate?.name || ''}`}
        primaryAction={{
          content: 'Save Changes',
          onAction: handleSaveTemplateEdit,
          loading: patchTemplates.actionLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleCloseEditModal,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {templateEditSuccess && (
              <Banner tone="success">
                <p>Template updated successfully!</p>
              </Banner>
            )}

            {editingTemplate?.previewUrl && (
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <img
                  src={editingTemplate.previewUrl}
                  alt={editingTemplate.name}
                  style={{ maxHeight: '120px', objectFit: 'contain', borderRadius: '8px' }}
                />
              </div>
            )}

            <TextField
              label="Template Name"
              value={editTemplateName}
              onChange={setEditTemplateName}
              autoComplete="off"
            />

            <TextField
              label="Stage 1 Prompt (Logo on Patch)"
              value={editTemplatePrompt}
              onChange={setEditTemplatePrompt}
              multiline={5}
              autoComplete="off"
              helpText="This prompt tells AI how to place the customer's logo onto this patch template."
              placeholder="e.g., Take this logo and place it on this embroidered patch. The logo should appear as if it was stitched with thread, following the contours and texture of the embroidery..."
            />

            <Text as="p" variant="bodySm" tone="subdued">
              Note: To replace the template image, delete this template and create a new one.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Follow-Up Rule Editor Modal */}
      <Modal
        open={!!editingFollowUpRule}
        onClose={() => setEditingFollowUpRule(null)}
        title={`Edit Rule: ${editingFollowUpRule ? STAGE_LABELS[editingFollowUpRule.stage] : ''} (${editingFollowUpRule?.timing || ''})`}
        primaryAction={{
          content: 'Save Changes',
          onAction: handleSaveEditRule,
          loading: followUpActionLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setEditingFollowUpRule(null),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {followUpTestSuccess && (
              <Banner tone="success">
                <p>{followUpTestSuccess}</p>
              </Banner>
            )}

            <InlineStack gap="200">
              <Badge tone={STAGE_TONES_MAP[editingFollowUpRule?.stage as WorkflowStage]}>
                {editingFollowUpRule ? STAGE_LABELS[editingFollowUpRule.stage] : ''}
              </Badge>
              <Tag>{editingFollowUpRule?.timing === 'immediate' ? 'Immediate' : 'Reminder'}</Tag>
              <Tag>{editingFollowUpRule?.audience === 'customer' ? 'Customer' : 'Staff'}</Tag>
            </InlineStack>

            {editingFollowUpRule?.description && (
              <Text as="p" variant="bodySm" tone="subdued">
                {editingFollowUpRule.description}
              </Text>
            )}

            <Select
              label="Channel"
              options={[
                { label: 'Email', value: 'email' },
                { label: 'SMS', value: 'sms' },
              ]}
              value={editRuleChannel}
              onChange={setEditRuleChannel}
            />

            <InlineStack gap="200" blockAlign="center">
              <Text as="span" variant="bodySm">Enabled</Text>
              <Button
                size="slim"
                variant={editRuleEnabled ? 'primary' : undefined}
                onClick={() => setEditRuleEnabled(!editRuleEnabled)}
              >
                {editRuleEnabled ? 'Yes' : 'No'}
              </Button>
            </InlineStack>

            {editingFollowUpRule?.timing === 'reminder' && (
              <InlineStack gap="400">
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Delay (hours)"
                    value={editRuleDelayHours}
                    onChange={setEditRuleDelayHours}
                    type="number"
                    autoComplete="off"
                    helpText="Hours before first reminder"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Repeat interval (hours)"
                    value={editRuleRepeatHours}
                    onChange={setEditRuleRepeatHours}
                    type="number"
                    autoComplete="off"
                    helpText="0 = don't repeat"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Max reminders"
                    value={editRuleMaxReminders}
                    onChange={setEditRuleMaxReminders}
                    type="number"
                    autoComplete="off"
                    helpText="0 = unlimited"
                  />
                </div>
              </InlineStack>
            )}

            {editRuleChannel === 'email' && (
              <TextField
                label="Subject Template"
                value={editRuleSubject}
                onChange={setEditRuleSubject}
                autoComplete="off"
              />
            )}

            {editRuleChannel === 'email' ? (
              <RichTextEditor
                label="Body Template"
                value={editRuleBody}
                onChange={setEditRuleBody}
              />
            ) : (
              <TextField
                label="SMS Text Template"
                value={editRuleBody}
                onChange={setEditRuleBody}
                multiline={6}
                autoComplete="off"
              />
            )}

            <Text as="p" variant="bodySm" tone="subdued">
              Available variables: {'{{customerName}}'}, {'{{customerFullName}}'}, {'{{orderName}}'}, {'{{stageName}}'}, {'{{daysSinceEntry}}'}, {'{{companyName}}'}
            </Text>

            <Divider />
            <Text as="h3" variant="headingSm">Send Test</Text>
            <InlineStack gap="200" blockAlign="end">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Order ID (Firestore doc ID)"
                  value={testOrderId}
                  onChange={setTestOrderId}
                  autoComplete="off"
                  placeholder="e.g., abc123"
                />
              </div>
              <Button
                size="slim"
                onClick={async () => {
                  if (!editingFollowUpRule || !testOrderId) return;
                  const result = await sendFollowUpTest(editingFollowUpRule.id, testOrderId);
                  if (result.success) {
                    setFollowUpTestSuccess(result.message || 'Test sent!');
                  } else {
                    setFollowUpTestSuccess(null);
                  }
                }}
                loading={followUpActionLoading}
                disabled={!testOrderId.trim()}
              >
                Send Test
              </Button>
            </InlineStack>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Gmail Account Connect Modal */}
      <Modal
        open={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        title="Connect Gmail Account"
        primaryAction={{
          content: 'Connect',
          onAction: handleConnectAccount,
          disabled: !newAccountLabel.trim(),
          loading: gmailAccountsActionLoading,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setConnectModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <TextField
            label="Account Label"
            value={newAccountLabel}
            onChange={setNewAccountLabel}
            autoComplete="off"
            placeholder="e.g., Sales, Jody, Support"
            helpText="A friendly name to identify this Gmail account"
          />
        </Modal.Section>
      </Modal>
    </Page>
  );
}
